import { Form, Formik, type FormikHelpers } from "formik";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  createTag,
  createTagGroup,
  deleteTag,
  deleteTagGroup,
  getTag,
  renameTagGroup,
  type Tag,
  type TagGroup,
  updateTag,
} from "@/features/settings/tags-api";
import { ApiError } from "@/lib/api";
import { applyApiErrors } from "@/lib/form-errors";

/** Tag names are code-like identifiers (used as keys everywhere they're applied). */
const TAG_NAME_RE = /^[A-Za-z0-9_]+$/;
const tagNameSchema = Yup.string()
  .trim()
  .required("Name is required")
  .max(25, "Keep it to 25 characters or fewer")
  .matches(TAG_NAME_RE, "Use letters, numbers and underscores only");

const groupNameSchema = Yup.string()
  .trim()
  .required("Name is required")
  .max(20, "Keep it to 20 characters or fewer");

// --- 1. Add Tag Group -----------------------------------------------------

interface NameValues {
  name: string;
}

export function AddTagGroupDialog({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (group: TagGroup) => void;
}) {
  async function handleSubmit(values: NameValues, { setErrors }: FormikHelpers<NameValues>) {
    try {
      const group = await createTagGroup(values.name.trim());
      onSaved(group);
      onOpenChange(false);
      toast.success("Category created");
    } catch (err) {
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't create the category." }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New tag category</DialogTitle>
          <DialogDescription>
            A category groups related tags (e.g. “Priority”, “Source”).
          </DialogDescription>
        </DialogHeader>
        <Formik<NameValues>
          initialValues={{ name: "" }}
          validationSchema={Yup.object({ name: groupNameSchema })}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <FormField name="name" label="Category Name" maxLength={20} />
              <DialogFooter>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving…" : "Create Category"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

// --- 2. Add Tag -----------------------------------------------------------

export function AddTagDialog({
  group,
  open,
  onOpenChange,
  onSaved,
}: {
  group: TagGroup | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (tag: Tag) => void;
}) {
  async function handleSubmit(values: NameValues, { setErrors }: FormikHelpers<NameValues>) {
    if (!group) return;
    try {
      const tag = await createTag(group.id, values.name.trim());
      onSaved(tag);
      onOpenChange(false);
      toast.success("Tag created");
    } catch (err) {
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't create the tag." }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New tag{group ? ` in “${group.name}”` : ""}</DialogTitle>
          <DialogDescription>
            Letters, numbers and underscores only — no spaces or punctuation.
          </DialogDescription>
        </DialogHeader>
        <Formik<NameValues>
          initialValues={{ name: "" }}
          validationSchema={Yup.object({ name: tagNameSchema })}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <FormField name="name" label="Tag Name" maxLength={25} />
              <DialogFooter>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving…" : "Create Tag"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

// --- 3. Rename Tag --------------------------------------------------------

interface RenameTagValues {
  name: string;
  applyToExisting: boolean;
}

export function RenameTagDialog({
  tag,
  open,
  onOpenChange,
  onSaved,
}: {
  tag: Tag | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (tag: Tag) => void;
}) {
  async function handleSubmit(
    values: RenameTagValues,
    { setErrors }: FormikHelpers<RenameTagValues>,
  ) {
    if (!tag) return;
    try {
      const saved = await updateTag(tag.id, { name: values.name.trim() });
      onSaved(saved);
      onOpenChange(false);
      toast.success("Tag renamed");
    } catch (err) {
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't rename the tag." }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename tag</DialogTitle>
          <DialogDescription>
            Renaming updates the tag everywhere it&apos;s already applied to clients.
          </DialogDescription>
        </DialogHeader>
        <Formik<RenameTagValues>
          initialValues={{ name: tag?.name ?? "", applyToExisting: true }}
          validationSchema={Yup.object({ name: tagNameSchema })}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form className="grid gap-5">
              <FormField name="name" label="Tag Name" maxLength={25} />
              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  checked={values.applyToExisting}
                  onCheckedChange={(c) => setFieldValue("applyToExisting", c === true)}
                  aria-label="Apply to existing clients"
                />
                <span className="text-muted-foreground">
                  Apply to existing clients (the rename always propagates to clients already
                  tagged).
                </span>
              </label>
              <DialogFooter>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving…" : "Save Changes"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

// --- 4. Delete Tag --------------------------------------------------------

export function DeleteTagDialog({
  tag,
  open,
  onOpenChange,
  onDeleted,
}: {
  tag: Tag | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: (id: string) => void;
}) {
  const [count, setCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  // Fetch the real client_count when the dialog opens (list rows carry null).
  useEffect(() => {
    if (!open || !tag) return;
    let active = true;
    setCount(null);
    getTag(tag.id)
      .then((t) => active && setCount(t.clientCount ?? 0))
      .catch(() => active && setCount(0));
    return () => {
      active = false;
    };
  }, [open, tag]);

  async function handleDelete() {
    if (!tag) return;
    setBusy(true);
    try {
      await deleteTag(tag.id);
      onDeleted(tag.id);
      onOpenChange(false);
      toast.success("Tag deleted");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't delete the tag.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete “{tag?.name}”?</AlertDialogTitle>
          <AlertDialogDescription>
            {count === null
              ? "Checking how many clients use this tag…"
              : `This tag is on ${count} client${count === 1 ? "" : "s"}. Deleting removes it from all of them. This can't be undone.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void handleDelete();
            }}
            disabled={busy}
            className="bg-destructive text-white hover:bg-destructive/90"
          >
            {busy ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// --- 5. Rename Category ---------------------------------------------------

export function RenameCategoryDialog({
  group,
  open,
  onOpenChange,
  onSaved,
}: {
  group: TagGroup | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (group: TagGroup) => void;
}) {
  async function handleSubmit(values: NameValues, { setErrors }: FormikHelpers<NameValues>) {
    if (!group) return;
    try {
      const saved = await renameTagGroup(group.id, values.name.trim());
      onSaved(saved);
      onOpenChange(false);
      toast.success("Category renamed");
    } catch (err) {
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't rename the category." }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename category</DialogTitle>
        </DialogHeader>
        <Formik<NameValues>
          initialValues={{ name: group?.name ?? "" }}
          validationSchema={Yup.object({ name: groupNameSchema })}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <FormField name="name" label="Category Name" maxLength={20} />
              <DialogFooter>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving…" : "Save Changes"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

// --- 6. Delete Category ---------------------------------------------------

export function DeleteCategoryDialog({
  group,
  open,
  onOpenChange,
  onDeleted,
}: {
  group: TagGroup | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: (id: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function handleDelete() {
    if (!group) return;
    setBusy(true);
    try {
      await deleteTagGroup(group.id);
      onDeleted(group.id);
      onOpenChange(false);
      toast.success("Category deleted");
    } catch (err) {
      // 400 = group still has tags; the server's detail is the actionable message.
      toast.error(
        err instanceof ApiError && err.status === 400
          ? err.message || "Delete or move this group's tags first."
          : err instanceof ApiError
            ? err.message
            : "Couldn't delete the category.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete “{group?.name}”?</AlertDialogTitle>
          <AlertDialogDescription>
            A category can only be deleted once it has no tags. Delete or move this group&apos;s
            tags first.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void handleDelete();
            }}
            disabled={busy}
            className="bg-destructive text-white hover:bg-destructive/90"
          >
            {busy ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
